import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSupportRpcClient } from "@redotech/redo-merchant-support-rpc/client";
import {
  ConversationStatus,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { CloseActionMethod, Team } from "@redotech/redo-model/team";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { Dispatch, memo, SetStateAction, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UpdateConversationStateContext } from "../context/update-conversations-context";

async function handleMarkTicketsSpam({
  supportRpcClient,
  team,
  conversations,
  setActiveConversation,
  setConversationsInProximity,
  navigate,
  inDetailView,
  cleanupFn,
  removeConversation,
  nextConversationInList,
  prevConversationInList,
}: {
  supportRpcClient: RedoSupportRpcClient;
  team: Team;
  conversations: MinimalConversation[];
  setActiveConversation: (val: MinimalConversation | undefined) => void;
  setConversationsInProximity?: Dispatch<SetStateAction<MinimalConversation[]>>;
  navigate: (url: string) => void;
  inDetailView: boolean;
  cleanupFn?: (clearSelected?: boolean) => void;
  removeConversation: (conversation: MinimalConversation) => void;
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
}) {
  await alertOnFailure("Failed to mark conversations as spam")(async () => {
    await supportRpcClient.markConversationsSpam({
      conversationIds: conversations.map((conversation) => conversation._id),
    });
  });

  if (setConversationsInProximity) {
    setConversationsInProximity((prev: MinimalConversation[]) =>
      prev.filter((conv) => !conversations.some((c) => c._id === conv._id)),
    );
  }

  for (const conversation of conversations) {
    if (conversation.status !== ConversationStatus.CLOSED) {
      removeConversation(conversation);
    }
  }

  if (
    conversations.length === 1 &&
    inDetailView &&
    team.settings.support?.closeAction
  ) {
    switch (team.settings.support?.closeAction) {
      case CloseActionMethod.NEXT: {
        setActiveConversation(nextConversationInList || prevConversationInList);
        break;
      }
      case CloseActionMethod.STAY: {
        setActiveConversation({
          ...conversations[0],
          status: ConversationStatus.CLOSED,
        });
        break;
      }
      case CloseActionMethod.TABLE: {
        const searchParams = new URLSearchParams(location.search);
        const ACTIVE_CONVERSATION_QUERY_PARAMETER = "activeConversationId";
        searchParams.delete(ACTIVE_CONVERSATION_QUERY_PARAMETER);
        navigate(`${location.pathname}?${searchParams}`);
        setActiveConversation(undefined);
        break;
      }
      default: {
        assertNever(team.settings.support?.closeAction);
      }
    }
  }

  amplitude.logEvent("markSpam-conversation", {
    mode: conversations.length === 1 ? "single" : "multiple",
    conversationIds: conversations.map((conversation) => conversation._id),
  });

  cleanupFn?.(true);
}

export const MarkSpamModal = memo(function MarkSpamModal({
  open,
  setOpen,
  conversations,
  setActiveConversation,
  setConversationsInProximity,
  inDetailView,
  cleanupFn,
  nextConversationInList,
  prevConversationInList,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  setActiveConversation: (val: MinimalConversation | undefined) => void;
  setConversationsInProximity?: Dispatch<SetStateAction<MinimalConversation[]>>;
  inDetailView: boolean;
  cleanupFn?: (clearSelected?: boolean) => void;
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
}) {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const { removeConversation } = useRequiredContext(
    UpdateConversationStateContext,
  );
  const markableConversations = useMemo(
    () =>
      conversations.filter((conversation) => {
        const primaryEmail = getPrimaryCustomerEmail(conversation.customer);

        return (
          conversation.status !== ConversationStatus.CLOSED ||
          !conversation.tagIds?.some((tag) => tag.name === "spam") ||
          (primaryEmail &&
            !team.settings.support?.excludedEmails?.includes(primaryEmail))
        );
      }),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversations],
  );
  const [markSpamLoad, doMarkSpam] = useTriggerLoad(async (signal) => {
    await handleMarkTicketsSpam({
      supportRpcClient,
      team,
      conversations: markableConversations,
      setActiveConversation,
      setConversationsInProximity,
      navigate,
      inDetailView,
      removeConversation,
      nextConversationInList,
      prevConversationInList,
    });
    setOpen(false);
  });

  const plural = markableConversations.length > 1;

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={() => {
        cleanupFn?.();
        setOpen(false);
      }}
      primaryButton={{
        text: `Yes, mark ticket${plural ? "s" : ""} as spam`,
        onClick: () => doMarkSpam(),
        pending: markSpamLoad.pending,
        theme: "normal",
        size: "sm",
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => setOpen(false),
        size: "sm",
      }}
      title={
        plural
          ? `Mark ${markableConversations.length} tickets as spam?`
          : "Mark ticket as spam?"
      }
    >
      <Text>
        {plural ? "These tickets" : "This ticket"} will be closed and tagged
        "spam", and the
        {plural ? " senders' email addresses " : " sender's email address "}
        will be added to your team's exclusion list.
      </Text>
    </RedoModal>
  );
});
